import { Theme } from '@/theme';
import styled from 'styled-components';
import { ButtonProps } from './button.types';

const colorLookup = {
  filled: {
    positive: {
      disabled: {
        borderColor: {
          default: 'transparent',
          hover: 'transparent',
          active: 'transparent',
        },
        backgroundColor: {
          default: 'ui5',
          hover: 'ui5',
          active: 'ui5',
        },
        color: {
          default: 'ui4',
          hover: 'ui4',
          active: 'ui4',
        },
      },
      enabled: {
        borderColor: {
          default: 'transparent',
          hover: 'transparent',
          active: 'transparent',
        },
        backgroundColor: {
          default: 'ui6',
          hover: 'ui6',
          active: 's1',
        },
        color: {
          default: 's1',
          hover: 's2',
          active: 'ui6',
        },
      },
    },
    negative: {
      disabled: {
        borderColor: {
          default: 'transparent',
          hover: 'transparent',
          active: 'transparent',
        },
        backgroundColor: {
          default: 'ui5',
          hover: 'ui5',
          active: 'ui5',
        },
        color: {
          default: 'ui4',
          hover: 'ui4',
          active: 'ui4',
        },
      },
      enabled: {
        borderColor: {
          default: 'transparent',
          hover: 'transparent',
          active: 'transparent',
        },
        backgroundColor: {
          default: 'ui6',
          hover: 'ui6',
          active: 'ui2',
        },
        color: {
          default: 'ui2',
          hover: 'ui2',
          active: 'ui6',
        },
      },
    },
  },
  outlined: {
    positive: {
      disabled: {
        borderColor: {
          default: 'ui4',
          hover: 'ui4',
          active: 'ui4',
        },
        backgroundColor: {
          default: 'ui5',
          hover: 'ui5',
          active: 'ui5',
        },
        color: {
          default: 'ui4',
          hover: 'ui4',
          active: 'ui4',
        },
      },
      enabled: {
        borderColor: {
          default: 's1',
          hover: 's2',
          active: 's1',
        },
        backgroundColor: {
          default: 'ui6',
          hover: 'ui6',
          active: 's1',
        },
        color: {
          default: 's1',
          hover: 's2',
          active: 'ui6',
        },
      },
    },
    negative: {
      disabled: {
        borderColor: {
          default: 'ui4',
          hover: 'ui4',
          active: 'ui4',
        },
        backgroundColor: {
          default: 'ui5',
          hover: 'ui5',
          active: 'ui5',
        },
        color: {
          default: 'ui4',
          hover: 'ui4',
          active: 'ui4',
        },
      },
      enabled: {
        borderColor: {
          default: 'ui2',
          hover: 'ui2',
          active: 'ui2',
        },
        backgroundColor: {
          default: 'ui6',
          hover: 'ui6',
          active: 'ui2',
        },
        color: {
          default: 'ui2',
          hover: 'ui2',
          active: 'ui6',
        },
      },
    },
  },
};

function getColor(
  color: Theme['color'],
  variant: ButtonProps['variant'],
  look: ButtonProps['look'],
  isDisabled: ButtonProps['isDisabled'],
  key: 'borderColor' | 'backgroundColor' | 'color',
  state: 'default' | 'hover' | 'active',
) {
  if (!variant || !look) return 'transparent';
  const disabled = isDisabled ? 'disabled' : 'enabled';
  const colorValue = colorLookup[variant][look][disabled][key][state];
  return color[colorValue as keyof Theme['color']];
}

export const Button = styled.button<{
  $look: ButtonProps['look'];
  $variant: ButtonProps['variant'];
  $width: ButtonProps['width'];
  $size: ButtonProps['size'];
}>`
  ${({ theme: { font }, $size }) => ($size === 'default' ? font.t3 : font.t5)};
  height: ${({ $size }) => ($size === 'default' ? '32px' : '28px')};
  width: ${({ $width }) => ($width === 'auto' ? 'max-content' : '100%')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: ${({ theme: { radius } }) => radius.lg};
  border-width: 2px;
  border-style: solid;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  box-shadow: ${({ disabled, theme: { shadows } }) => (disabled ? 'none' : shadows.standByShadow)};
  transition: 75ms all;
  gap: 12px;

  // Applying color map
  border-color: ${({ theme: { color }, $variant, disabled, $look }) =>
    getColor(color, $variant, $look, disabled, 'borderColor', 'default')};
  background-color: ${({ theme: { color }, disabled, $variant, $look }) =>
    getColor(color, $variant, $look, disabled, 'backgroundColor', 'default')};
  color: ${({ theme: { color }, $look, disabled, $variant }) =>
    getColor(color, $variant, $look, disabled, 'color', 'default')};
  fill: ${({ theme: { color }, $look, disabled, $variant }) =>
    getColor(color, $variant, $look, disabled, 'color', 'default')};

  &:hover {
    box-shadow: ${({ theme: { shadows } }) => shadows.outerShadow};

    // Applying color map
    border-color: ${({ theme: { color }, $variant, disabled, $look }) =>
      getColor(color, $variant, $look, disabled, 'borderColor', 'hover')};
    background-color: ${({ theme: { color }, $look, disabled, $variant }) =>
      getColor(color, $variant, $look, disabled, 'backgroundColor', 'hover')};
    color: ${({ theme: { color }, $look, disabled, $variant }) =>
      getColor(color, $variant, $look, disabled, 'color', 'hover')};
    fill: ${({ theme: { color }, $look, disabled, $variant }) =>
      getColor(color, $variant, $look, disabled, 'color', 'hover')};
  }
  &:active {
    box-shadow: ${({ theme: { shadows } }) => shadows.innerShadowDark};

    // Applying color map
    border-color: ${({ theme: { color }, $variant, disabled, $look }) =>
      getColor(color, $variant, $look, disabled, 'borderColor', 'active')};
    background-color: ${({ theme: { color }, $look, disabled, $variant }) =>
      getColor(color, $variant, $look, disabled, 'backgroundColor', 'active')};
    color: ${({ theme: { color }, $look, disabled, $variant }) =>
      getColor(color, $variant, $look, disabled, 'color', 'active')};
    fill: ${({ theme: { color }, $look, disabled, $variant }) =>
      getColor(color, $variant, $look, disabled, 'color', 'active')};
  }
`;

export const IconWrapper = styled.div`
  box-sizing: border-box;
  fill: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  & > * {
    width: 100%;
    height: 100%;
  }
`;
